import { KatAlert, KatBadge, KatIcon } from "@amzn/katal-react";
import React from "react";
import { useTranslation } from "react-i18next";
import Markdown from "markdown-to-jsx";
import type {
  LocalizedText,
  NestStyle,
  NestUseCase,
  NestUseCaseDocumentationCommonErrors,
} from "src/api/interfaces/nest";
import { NestPanel } from "../NestPanel";
import "./index.css";

const NEST_STYLE_COLORS: Record<NestStyle, string> = {
  RED: "var(--kat-red-500)",
  YELLOW: "var(--kat-yellow-400)",
  ORANGE: "var(--kat-orange-400)",
  BLUE: "var(--kat-blue-500)",
};

const NEST_STYLE_BADGE_TYPES: Record<NestStyle, KatBadge.Type> = {
  RED: "danger",
  YELLOW: "warning",
  ORANGE: "warning",
  BLUE: "info",
};

interface NestExpectationsPanelProps {
  useCase: NestUseCase;
  documentTypeName: LocalizedText;
  onClose?: () => void;
}

export function NestExpectationsPanel({
  useCase,
  documentTypeName,
  onClose,
}: NestExpectationsPanelProps) {
  const { t } = useTranslation();
  const { documentation, name } = useCase;
  const resolvedName = t(name.key, name.parameters);
  const resolvedFormat = t(documentTypeName.key, documentTypeName.parameters);

  return (
    <NestPanel
      title={t("aic_nest_expectations_title", { name: resolvedName })}
      onClose={onClose}
      closeAriaLabel={t("aic_nest_expectations_close_label")}
    >
      <div className="nest-expectations-content">
        <KatAlert
          variant="info"
          persistent
          header={t("aic_nest_expectations_format_label", {
            format: resolvedFormat,
          })}
          description={t("aic_nest_expectations_format_description")}
        />

        <div>
          <h3 className="nest-expectations-section-title">
            <KatIcon
              name="check_circle"
              size="small"
              className="nest-expectations-icon nest-expectations-icon--check"
            />
            {t("aic_nest_expectations_what_we_expect")}
          </h3>
          <Markdown>
            {t(documentation.content.key, documentation.content.parameters)}
          </Markdown>
        </div>

        {documentation.commonErrors.length > 0 && (
          <div>
            <h3 className="nest-expectations-section-title">
              <KatIcon
                name="warning"
                size="small"
                className="nest-expectations-icon nest-expectations-icon--warning"
              />
              {t("aic_nest_expectations_common_errors")}
            </h3>
            {documentation.commonErrors.map((error, i) => (
              <ErrorCard key={i} error={error} />
            ))}
          </div>
        )}
      </div>
    </NestPanel>
  );
}

function ErrorCard({ error }: { error: NestUseCaseDocumentationCommonErrors }) {
  const { t } = useTranslation();

  return (
    <div
      className="nest-expectations-error-card"
      style={{ borderLeftColor: NEST_STYLE_COLORS[error.style] }}
    >
      <Markdown>{t(error.content.key, error.content.parameters)}</Markdown>
      {error.tags.length > 0 && (
        <div className="nest-expectations-tags">
          {error.tags.map((tag, j) => (
            <KatBadge
              key={j}
              className="nest-expectations-tag"
              label={t(tag.name.key, tag.name.parameters)}
              type={NEST_STYLE_BADGE_TYPES[tag.style]}
            />
          ))}
        </div>
      )}
    </div>
  );
}
